/**
 * Shared building blocks for the three "Uni *" engagement modules
 * (Uni Email, Uni SMS, Uni Forms).
 *
 * These three modules ship together, look-and-feel the same, and hit
 * very similar JSON contracts (DRF DefaultRouter + paginated list
 * responses).  Pulling the cross-cutting primitives into one file
 * keeps each per-app screen tiny and consistent.  The visual
 * language matches the rest of Taskydo (the ``ty-*`` design tokens
 * defined in ``src/styles/global.css``); module-specific extras
 * live in ``./uni.css``.
 *
 * Auth & multi-tenancy
 * --------------------
 * Every API call goes through ``src/api/client.js`` → ``axios.js``,
 * which:
 *   * Attaches the JWT bearer token from localStorage.
 *   * Sends to the current origin (i.e. the tenant subdomain) so
 *     django-tenants resolves the right schema.
 *   * Refreshes expired access tokens automatically.
 *   * Routes 401 → /login.
 *
 * UI gating is provided by the route guard in ``App.jsx``: only users
 * whose ``permitted_apps`` contains the relevant key (``uni-email`` /
 * ``uni-sms`` / ``uni-forms``) — or who are super-admins / org
 * admins — can ever land here.  Backend re-checks every request via
 * ``IsAuthenticated`` on the DRF viewset and via the
 * ``TenantUserBindingMiddleware`` org-id check.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { deleteJSON, getJSON, patchJSON, postJSON } from '../../api/client.js';
import SortableTh from '../../components/SortableTh.jsx';
import {
  columnIsSortable, columnSortValue, nextSortState, sortRows,
} from '../../lib/tableSort.js';
import { pageWindow } from './uniPager.js';

import './uni.css';


/* ====================================================================
   Page chrome
   ==================================================================== */

/**
 * UniPageShell — full-bleed page chrome with a coloured hero banner.
 *
 * Props
 * -----
 * theme    : 'email' | 'sms' | 'forms' | 'waba' | 'rag'  (drives accent colour)
 * icon     : emoji / element shown in the rounded chip on the left
 * title    : page title
 * subtitle : optional one-liner under the title
 * actions  : optional element rendered in the top-right of the hero
 * helpPage : help-catalog key (shown under the hero on every screen)
 * helpApi  : '/api/waba-chat' or '/api/rag' (no trailing slash)
 * helpOpen : whether the help banner starts expanded (default true).
 *            SignalBridge everyday screens pass false so the first
 *            thing users see is the task, not a wall of copy.
 * children : page body
 */
export function UniPageShell({
  theme = 'email', icon, title, subtitle, actions, children, helpPage, helpApi,
  helpOpen = true,
}) {
  return (
    <div className={`uni-${theme}`} style={{ padding: '20px 24px' }}>
      <header className="uni-hero">
        {icon && <div className="uni-hero__icon" aria-hidden="true">{icon}</div>}
        <div className="uni-hero__body">
          <h1 className="uni-hero__title">{title}</h1>
          {subtitle && <div className="uni-hero__sub">{subtitle}</div>}
        </div>
        {actions && <div className="uni-hero__actions">{actions}</div>}
      </header>
      {helpPage && helpApi ? (
        <HelpBanner apiBase={helpApi} page={helpPage} defaultOpen={helpOpen} />
      ) : null}
      <div>{children}</div>
    </div>
  );
}


/**
 * Stage-by-stage help copy from ``/api/<module>/help/?page=``.
 * WhatsApp and RAG require this on every screen (web + the same JSON
 * on mobile). Other Uni pages may opt in via UniPageShell props.
 */
export function HelpBanner({ apiBase, page, defaultOpen = true }) {
  const [help, setHelp] = React.useState(null);
  const [open, setOpen] = React.useState(defaultOpen);

  React.useEffect(() => {
    setOpen(defaultOpen);
  }, [page, defaultOpen]);

  React.useEffect(() => {
    if (!apiBase || !page) return undefined;
    let cancelled = false;
    const base = String(apiBase).replace(/\/$/, '');
    getJSON(`${base}/help/?page=${encodeURIComponent(page)}`)
      .then((data) => { if (!cancelled) setHelp(data); })
      .catch(() => { if (!cancelled) setHelp(null); });
    return () => { cancelled = true; };
  }, [apiBase, page]);

  if (!help) return null;
  const steps = Array.isArray(help.steps) ? help.steps : [];
  return (
    <aside className="uni-help" aria-label="Help">
      <div className="uni-help__bar">
        <strong className="uni-help__title">{help.title || 'Help'}</strong>
        <button
          type="button"
          className="uni-help__toggle"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Hide help' : 'Show help'}
        </button>
      </div>
      {open && (
        <div className="uni-help__body">
          {help.body ? <p className="uni-help__copy">{help.body}</p> : null}
          {steps.length > 0 && (
            <ol className="uni-help__steps">
              {steps.map((step, i) => <li key={i}>{step}</li>)}
            </ol>
          )}
        </div>
      )}
    </aside>
  );
}


/* ====================================================================
   Action button — themed for the coloured hero (white-on-banner) and
   for ordinary in-page use (uses the global ``.ty-btn`` family).
   ==================================================================== */

export function UniActionButton({ to, onClick, primary, hero, disabled, children }) {
  // ``hero`` makes the button styled for the coloured hero banner
  // (white-on-accent) — used on the dashboards. CSS also restyles
  // these when they appear in the page body so they stay readable.
  if (hero) {
    const cls = primary ? 'uni-hero-btn uni-hero-btn--solid' : 'uni-hero-btn';
    if (to) return <Link to={to} className={cls}>{children}</Link>;
    return <button type="button" className={cls} onClick={onClick} disabled={disabled}>{children}</button>;
  }
  // Default look — global pill button.
  const cls = 'ty-btn ' + (primary ? '' : 'ty-btn--secondary');
  if (to) return <Link to={to} className={cls.trim()}>{children}</Link>;
  return <button type="button" className={cls.trim()} onClick={onClick} disabled={disabled}>{children}</button>;
}


/* ====================================================================
   Status pill
   ==================================================================== */

/** Maps a status string to one of the seven ``.uni-pill--*`` colours. */
function statusPalette(value) {
  const v = String(value || '').toLowerCase();
  if (['draft', 'pending', 'queued', 'scheduled'].includes(v)) return 'blue';
  if (['sent', 'sending', 'completed', 'active', 'approved', 'delivered', 'open'].includes(v)) return 'green';
  if (['failed', 'bounced', 'rejected', 'cancelled', 'error'].includes(v)) return 'red';
  if (['paused', 'suspended', 'expired'].includes(v)) return 'amber';
  if (['archived', 'unsubscribed', 'inactive', 'closed'].includes(v)) return 'gray';
  if (['promotional', 'transactional'].includes(v)) return 'violet';
  return 'cyan';
}

export function UniStatusPill({ value, palette }) {
  if (value === null || value === undefined || value === '') return null;
  const p = palette || statusPalette(value);
  return (
    <span className={`uni-pill uni-pill--${p}`}>
      {String(value).replace(/_/g, ' ')}
    </span>
  );
}


/* ====================================================================
   Boolean cell — ✓ / —
   ==================================================================== */

export function UniBool({ value }) {
  return value
    ? <span className="uni-check" aria-label="yes">✓</span>
    : <span className="uni-check uni-check--off" aria-label="no">—</span>;
}


/* ====================================================================
   KPI card
   ==================================================================== */

export function UniKpiCard({
  icon = '📊', label, value, sublabel, to, onClick, linkTitle,
}) {
  const body = (
    <>
      <div className="uni-kpi__icon" aria-hidden="true">{icon}</div>
      <div className="uni-kpi__body">
        <div className="uni-kpi__label">{label}</div>
        <div className="uni-kpi__value">
          {value === null || value === undefined ? '—' : value}
        </div>
        {sublabel && <div className="uni-kpi__sub">{sublabel}</div>}
      </div>
      {(to || onClick) && (
        <div className="uni-kpi__chevron" aria-hidden="true">›</div>
      )}
    </>
  );

  // A figure the user can act on should be reachable, so the card
  // becomes the link rather than growing a separate "view" affordance
  // next to it — the whole tile is a bigger, more obvious target.
  if (to) {
    return (
      <Link to={to} className="uni-kpi uni-kpi--link"
            title={linkTitle || `Open ${label}`}>
        {body}
      </Link>
    );
  }

  // ``onClick`` covers the same-page case (e.g. jumping to a section
  // further down).  Rendered as a real <button> so keyboard and screen
  // readers treat it like the control it is.
  if (onClick) {
    return (
      <button type="button" className="uni-kpi uni-kpi--link"
              onClick={onClick} title={linkTitle || `Go to ${label}`}>
        {body}
      </button>
    );
  }

  return <div className="uni-kpi">{body}</div>;
}



/* ====================================================================
   Section divider with a small uppercase title
   ==================================================================== */

export function UniSection({ title, id }) {
  return (
    <div className="uni-section" id={id}>
      <h2 className="uni-section__title">{title}</h2>
      <div className="uni-section__line" />
    </div>
  );
}


/* ====================================================================
   Tile (used on dashboards as a quick-link card)
   ==================================================================== */

export function UniTile({ to, icon, title, sub }) {
  return (
    <Link to={to} className="uni-tile">
      <div className="uni-tile__icon" aria-hidden="true">{icon}</div>
      <div className="uni-tile__body">
        <div className="uni-tile__title">{title}</div>
        {sub && <div className="uni-tile__sub">{sub}</div>}
      </div>
      <div className="uni-tile__chevron" aria-hidden="true">›</div>
    </Link>
  );
}


/* ====================================================================
   Table — uses the global ``.ty-table`` tokens.  Loading / empty /
   error are rendered inside ``.uni-state`` cards so they look at
   home with the rest of the surface.

   Column headings are clickable sort controls (toggle asc/desc).
   Pass ``sortable={false}`` to disable, ``sortable: false`` on a
   column to skip it, or ``sortKey`` / ``sortDir`` / ``onSort`` for
   a parent-controlled (usually server-side) sort.
   ==================================================================== */

export function UniTable({
  columns, rows, loading, error, empty, emptyHint, rowKey, caption, rowExpand,
  sortable = true, sortKey, sortDir, onSort, getSortValue,
}) {
  const controlled = typeof onSort === 'function';
  const [internal, setInternal] = React.useState({ key: null, dir: 'asc' });
  const activeKey = controlled ? (sortKey ?? null) : internal.key;
  const activeDir = controlled ? (sortDir || 'asc') : internal.dir;

  const displayed = React.useMemo(() => {
    if (controlled || !sortable || !activeKey || !Array.isArray(rows) || rows.length === 0) {
      return rows || [];
    }
    return sortRows(rows, {
      dir: activeDir,
      getValue: (row) => {
        const col = (columns || []).find((c) => c.key === activeKey);
        if (typeof getSortValue === 'function') {
          const override = getSortValue(row, col || { key: activeKey });
          if (override !== undefined) return override;
        }
        return col ? columnSortValue(col, row) : row?.[activeKey];
      },
    });
  }, [controlled, sortable, activeKey, activeDir, rows, columns, getSortValue]);

  const handleSort = (key) => {
    const next = nextSortState({ key: activeKey, dir: activeDir }, key);
    if (controlled) onSort(next.key, next.dir);
    else setInternal(next);
  };

  if (loading) {
    return (
      <div className="uni-state">
        <div className="ty-spinner" aria-hidden="true" />
        <div className="uni-state__hint">Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ty-error">
        <div className="ty-error__head">
          <span className="ty-error__icon">⚠️</span>
          Couldn't load this list.
        </div>
        <div className="ty-error__trail">{String(error.message || error)}</div>
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="uni-state">
        <div className="uni-state__icon">📭</div>
        <div className="uni-state__title">{empty || 'Nothing here yet.'}</div>
        {emptyHint && <div className="uni-state__hint">{emptyHint}</div>}
      </div>
    );
  }
  return (
    <div className="uni-table ty-table-wrap">
      <table className="ty-table">
        {caption && <caption className="ty-page-header__subtitle" style={{ captionSide: 'top', textAlign: 'left', padding: '0 0 8px' }}>{caption}</caption>}
        <thead>
          <tr>
            {columns.map((c) => (
              <SortableTh
                key={c.key}
                label={c.label}
                sortKey={c.key}
                activeKey={activeKey}
                direction={activeDir}
                onSort={handleSort}
                sortable={sortable && columnIsSortable(c)}
                align={c.align || 'left'}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {displayed.map((r, i) => {
            const key = typeof rowKey === 'function'
              ? rowKey(r, i)
              : ((rowKey && r[rowKey]) || i);
            const extra = rowExpand ? rowExpand(r) : null;
            return (
              <React.Fragment key={key}>
                <tr>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      data-label={c.label}
                      style={{ textAlign: c.align || 'left' }}
                    >
                      {c.render ? c.render(r) : (r[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
                {extra ? (
                  <tr className="uni-table__expand">
                    <td colSpan={columns.length} data-label="JSON">{extra}</td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


/* ====================================================================
   Toolbar with optional search input.  ``onSearch`` is fired
   debounced (250ms) so callers can wire it directly to a list
   query.  When omitted the search box is just a visual filler.
   ==================================================================== */

export function UniToolbar({ search, onSearch, placeholder, children }) {
  const [val, setVal] = React.useState(search || '');
  React.useEffect(() => { setVal(search || ''); }, [search]);
  React.useEffect(() => {
    if (!onSearch) return;
    const t = setTimeout(() => onSearch(val), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [val]);

  return (
    <div className="uni-toolbar">
      {onSearch !== undefined && (
        <label className="uni-search" aria-label="Search">
          <input
            type="search"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder={placeholder || 'Search…'}
          />
        </label>
      )}
      {children}
    </div>
  );
}


/* ====================================================================
   List pager — Previous / numbered pages / Next, plus optional
   page-size.  Talks to DRF ``PageNumberPagination`` (``page``,
   ``page_size``, ``count``).  Hidden when there is nothing to page.
   ==================================================================== */

export { pageWindow } from './uniPager.js';

export function UniPager({
  page, pageSize, count, onPage, onPageSize, disabled,
  pageSizeOptions = [10, 20, 50, 100],
}) {
  const total = Number(count) || 0;
  const size = Math.max(1, Number(pageSize) || 20);
  const current = Math.max(1, Number(page) || 1);
  const totalPages = Math.max(1, Math.ceil(total / size));
  if (total === 0) return null;
  const from = (current - 1) * size + 1;
  const to = Math.min(current * size, total);
  const go = (next) => {
    if (disabled || typeof onPage !== 'function') return;
    const clamped = Math.min(totalPages, Math.max(1, next));
    if (clamped !== current) onPage(clamped);
  };

  return (
    <nav className="uni-pager" aria-label="Pagination">
      <span className="uni-pager__status">
        {from}–{to} of {fmtCount(total)}
        {totalPages > 1 ? ` · Page ${current} of ${fmtCount(totalPages)}` : ''}
      </span>
      <div className="uni-pager__nav">
        <button
          type="button"
          className="ty-btn ty-btn--secondary"
          onClick={() => go(current - 1)}
          disabled={disabled || current <= 1}
        >
          Previous
        </button>
        {pageWindow(current, totalPages).map((item, idx) => (
          item === '…' ? (
            <span key={`gap-${idx}`} className="uni-pager__gap" aria-hidden="true">…</span>
          ) : (
            <button
              key={item}
              type="button"
              className={`uni-pager__num${item === current ? ' is-active' : ''}`}
              onClick={() => go(item)}
              disabled={disabled || item === current}
              aria-current={item === current ? 'page' : undefined}
              aria-label={`Page ${item}`}
            >
              {item}
            </button>
          )
        ))}
        <button
          type="button"
          className="ty-btn ty-btn--secondary"
          onClick={() => go(current + 1)}
          disabled={disabled || current >= totalPages}
        >
          Next
        </button>
      </div>
      {typeof onPageSize === 'function' && (
        <label className="uni-pager__size">
          Per page
          <select
            value={size}
            disabled={disabled}
            onChange={(e) => onPageSize(Number(e.target.value))}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
      )}
    </nav>
  );
}


/* ====================================================================
   Generic list-fetch hook.
   ====================================================================
   * ``url`` is appended to the API base by ``getJSON`` so callers pass
   * the relative path (e.g. ``/api/uni-email/contacts/``).  The hook
   * unwraps DRF's ``{count, next, previous, results}`` paginated shape
   * and returns the rows on ``state.rows``; the original payload is
   * still on ``state.raw`` for callers that need ``count`` / ``next``.
   */
export function useUniList(url, deps = []) {
  const [state, setState] = React.useState({
    rows: [], raw: null, loading: true, error: null,
  });

  const load = React.useCallback(() => {
    if (!url) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    getJSON(url)
      .then((data) => {
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];
        setState({ rows, raw: data, loading: false, error: null });
      })
      .catch((error) => setState({ rows: [], raw: null, loading: false, error }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ...deps]);

  React.useEffect(load, [load]);

  return { ...state, reload: load };
}


/* ====================================================================
   Modal — a small centred dialog used by the "create" flows.
   ====================================================================
   Deliberately dependency-free (no portal library): the Uni pages are
   rendered inside the app shell and a fixed-position overlay is enough.
   Closes on backdrop click and on Escape; the body scroll is locked
   while open so the page behind doesn't drift.
   */

export function UniModal({ open, title, onClose, children, footer, width = 520 }) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="uni-modal-backdrop" onClick={onClose}>
      <div
        className="uni-modal"
        style={{ maxWidth: width }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="uni-modal__head">
          <h3 className="uni-modal__title">{title}</h3>
          <button
            type="button"
            className="uni-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="uni-modal__body">{children}</div>
        {footer && <div className="uni-modal__foot">{footer}</div>}
      </div>
    </div>
  );
}


/* ====================================================================
   Field helpers used inside the create modals.  Thin wrappers over the
   global ``.ty-field`` / ``.ty-input`` tokens so each page's JSX stays
   readable.
   ====================================================================
   Markup still uses a real ``<label for=…>`` inside ``.ty-field`` so
   ``global.css`` caption styles apply.  An optional ``?`` sits beside
   the caption and reveals what the field is and where to copy it from.
   ``React.useId`` gives each field a collision-free id so the label
   stays clickable.
   */

/** Normalise field-level help: a string, or `{ title, body, where }`. */
export function normalizeFieldHelp(help) {
  if (!help) return null;
  if (typeof help === 'string') {
    const body = help.trim();
    return body ? { title: '', body, where: '' } : null;
  }
  const title = String(help.title || '').trim();
  const body = String(help.body || help.text || '').trim();
  const where = String(help.where || '').trim();
  if (!title && !body && !where) return null;
  return { title, body, where };
}

function useFieldHelp(help) {
  const [open, setOpen] = React.useState(false);
  const helpId = React.useId();
  const normalized = normalizeFieldHelp(help);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      setOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  const toggle = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((v) => !v);
  }, []);

  return { open, helpId, normalized, toggle };
}

export function FieldHelpPanel({ id, help }) {
  const n = normalizeFieldHelp(help);
  if (!n) return null;
  return (
    <div id={id} className="uni-field-help__panel" role="note">
      {n.title ? <strong className="uni-field-help__heading">{n.title}</strong> : null}
      {n.body ? <p className="uni-field-help__body">{n.body}</p> : null}
      {n.where ? (
        <p className="uni-field-help__where">
          <strong>Where to get it</strong>
          {n.where}
        </p>
      ) : null}
    </div>
  );
}

export function FieldHelpButton({ label, open, onToggle, controlsId }) {
  return (
    <button
      type="button"
      className={`uni-field-help__btn${open ? ' is-open' : ''}`}
      aria-label={`Help: ${label || 'this field'}`}
      aria-expanded={open}
      aria-controls={open ? controlsId : undefined}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onToggle}
    >
      ?
    </button>
  );
}

/**
 * Caption row with an optional ``?`` that reveals what the field is
 * and where to copy the value from. Used by create/edit modals and
 * SignalBridge wizards.
 */
export function FieldLabel({ htmlFor, label, required, help }) {
  const { open, helpId, normalized, toggle } = useFieldHelp(help);
  return (
    <>
      <div className="uni-field__label-row">
        <label htmlFor={htmlFor}>
          {label}
          {required ? <span className="uni-req" aria-hidden="true"> *</span> : null}
        </label>
        {normalized ? (
          <FieldHelpButton
            label={label}
            open={open}
            onToggle={toggle}
            controlsId={helpId}
          />
        ) : null}
      </div>
      {open && normalized ? <FieldHelpPanel id={helpId} help={normalized} /> : null}
    </>
  );
}

export function UniField({ label, hint, help, required, htmlFor, children }) {
  return (
    <div className="ty-field">
      <FieldLabel htmlFor={htmlFor} label={label} required={required} help={help} />
      {children}
      {hint && <span className="uni-field__hint">{hint}</span>}
    </div>
  );
}

export function UniTextField({
  label, value, onChange, placeholder, required, hint, help, autoFocus, type = 'text',
}) {
  const id = React.useId();
  const isSecret = type === 'password';
  return (
    <UniField label={label} hint={hint} help={help} required={required} htmlFor={id}>
      <input
        id={id}
        className="ty-input"
        type={isSecret ? 'password' : type}
        autoComplete={isSecret ? 'new-password' : undefined}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
      />
    </UniField>
  );
}

/** Wall-clock picker for scheduled reminders (HTML ``type=time``). */
export function UniTimeField({ label, value, onChange, hint, help, required }) {
  const id = React.useId();
  return (
    <UniField label={label} hint={hint} help={help} required={required} htmlFor={id}>
      <input
        id={id}
        className="ty-input uni-time-input"
        type="time"
        step="60"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label || 'Notification time'}
      />
    </UniField>
  );
}

export function UniTextArea({ label, value, onChange, placeholder, hint, help, rows = 3 }) {
  const id = React.useId();
  return (
    <UniField label={label} hint={hint} help={help} htmlFor={id}>
      <textarea
        id={id}
        className="ty-textarea"
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ minHeight: 'unset' }}
      />
    </UniField>
  );
}

export function UniSelectField({ label, value, onChange, options, hint, help, required }) {
  const id = React.useId();
  return (
    <UniField label={label} hint={hint} help={help} required={required} htmlFor={id}>
      <select
        id={id}
        className="ty-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </UniField>
  );
}

export function UniCheckField({ label, checked, onChange, hint, help }) {
  const { open, helpId, normalized, toggle } = useFieldHelp(help);
  const id = React.useId();
  return (
    <div className="uni-check-field-wrap">
      <div className="uni-check-field">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="uni-check-field__label-row">
          <label htmlFor={id} className="uni-check-field__label">{label}</label>
          {normalized ? (
            <FieldHelpButton
              label={label}
              open={open}
              onToggle={toggle}
              controlsId={helpId}
            />
          ) : null}
        </span>
      </div>
      {open && normalized ? <FieldHelpPanel id={helpId} help={normalized} /> : null}
      {hint && <span className="uni-field__hint">{hint}</span>}
    </div>
  );
}


/* ====================================================================
   useUniCreate — shared "create a record" state machine.
   ====================================================================
   Every Uni create modal needs the same four things: a draft object, a
   busy flag while the POST is in flight, the server's error message on
   failure, and a reset when the modal re-opens.  Rather than repeat
   that in each page, this hook owns it.

   ``onCreated`` is called with the created record so the caller can
   reload its list (and, when it makes sense, navigate to the new row).
   */

export function useUniCreate(url, initial, onCreated) {
  const [open, setOpen]     = React.useState(false);
  const [draft, setDraft]   = React.useState(initial);
  const [busy, setBusy]     = React.useState(false);
  const [error, setError]   = React.useState(null);

  const start = React.useCallback(() => {
    setDraft(initial);
    setError(null);
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initial)]);

  const close = React.useCallback(() => {
    if (busy) return;      // don't yank the modal out from under a POST
    setOpen(false);
    setError(null);
  }, [busy]);

  const set = React.useCallback((key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const submit = React.useCallback(async (payload) => {
    setBusy(true);
    setError(null);
    try {
      const created = await postJSON(url, payload ?? draft);
      setOpen(false);
      onCreated?.(created);
      return created;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setBusy(false);
    }
  }, [url, draft, onCreated]);

  return { open, draft, busy, error, start, close, set, setDraft, submit };
}


/* --------------------------------------------------------------------
   Inline error strip shown inside the create modals.
   --------------------------------------------------------------------
   ``client.js`` turns a failed request into
   ``Error('HTTP 400: {"title":["This field is required."]}')`` and
   hangs the parsed body off ``error.data``.  Dumping that raw JSON at
   a user is unhelpful, so when the body looks like DRF's
   ``{field: [messages]}`` validation shape we render one readable
   line per field instead.  Anything else (500s, network errors) falls
   back to the plain message.
   */

/** Turn a DRF error body into ``[['Field', 'message'], …]`` or null. */
function fieldErrors(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const pairs = Object.entries(data)
    // ``detail`` is DRF's non-field error key — not a form field.
    .filter(([k]) => k !== 'detail')
    .map(([field, msgs]) => {
      const text = Array.isArray(msgs) ? msgs.join(' ') : String(msgs);
      const label = field
        .replace(/_/g, ' ')
        .replace(/^./, (c) => c.toUpperCase());
      return [label, text];
    });
  return pairs.length ? pairs : null;
}

export function UniFormError({ error }) {
  if (!error) return null;
  const fields = fieldErrors(error.data);
  return (
    <div className="ty-error" style={{ marginBottom: 12 }}>
      <div className="ty-error__head">
        <span className="ty-error__icon">⚠️</span>
        Couldn't save.
      </div>
      {fields ? (
        <ul className="uni-error-list">
          {fields.map(([label, text]) => (
            <li key={label}><strong>{label}:</strong> {text}</li>
          ))}
        </ul>
      ) : (
        <div className="ty-error__trail">{String(error.message || error)}</div>
      )}
    </div>
  );
}


/* ====================================================================
   Tiny formatters reused across the modules
   ==================================================================== */

/** Format an integer with thousand-separators; null / undefined → '—'. */
export function fmtCount(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString();
}

/** Format a 0..1 (or 0..100) ratio as ``XX.X%``.  Accepts either. */
export function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  // If it looks like 0..1, scale up; otherwise assume already in %.
  const pct = n <= 1 ? n * 100 : n;
  return pct.toFixed(1) + '%';
}

/** ISO (YYYY-MM-DD) for a Date — the shape every date input / API wants. */
export function isoDate(d = new Date()) {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

/** Local calendar date as ``YYYY.MM.DD`` (report PDF download names). */
export function dottedDate(d = new Date()) {
  return isoDate(d).replace(/-/g, '.');
}


/* ====================================================================
   Date field
   ====================================================================
   A plain ``<input type="date">`` rather than a picker library: the
   value is already the ISO string the API expects, so there is no
   parse/format round-trip to get wrong, and the browser gives us
   localisation and keyboard support for free.
   */

export function UniDateField({ label, value, onChange, hint, required, max, min }) {
  const id = React.useId();
  return (
    <UniField label={label} hint={hint} required={required} htmlFor={id}>
      <input
        id={id}
        className="ty-input"
        type="date"
        value={value || ''}
        max={max}
        min={min}
        onChange={(e) => onChange(e.target.value)}
      />
    </UniField>
  );
}


/* ====================================================================
   Compact filter select — for toolbars rather than modals.
   ==================================================================== */

export function UniFilterSelect({ label, value, onChange, options, width }) {
  const id = React.useId();
  return (
    <span className="uni-filter">
      {label && <label className="uni-filter__label" htmlFor={id}>{label}</label>}
      <select
        id={id}
        className="ty-select uni-filter__select"
        style={width ? { width } : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </span>
  );
}


/* ====================================================================
   Pick list — a scrolling, searchable set of checkboxes.
   ====================================================================
   Used everywhere the user picks *many* of something (people for an
   audience, columns for a report).  A native ``<select multiple>`` is
   deliberately avoided: ctrl-clicking to keep a selection is hostile on
   touch devices and silently loses the whole set on a mis-click, which
   matters when the list is "who is allowed to fill this form".

   ``options`` is ``[{ value, label, sub, disabled }]``; ``selected`` is
   an array of values.  The parent owns the selection so it can be
   submitted as part of a bigger draft.
   */

export function UniPickList({
  label, hint, options, selected, onChange,
  searchable = true, placeholder = 'Search…', height = 190, emptyText,
}) {
  const [q, setQ] = React.useState('');
  const chosen = React.useMemo(
    () => new Set((selected || []).map(String)), [selected],
  );

  const visible = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => (
      String(o.label || '').toLowerCase().includes(needle)
      || String(o.sub || '').toLowerCase().includes(needle)
    ));
  }, [options, q]);

  const toggle = (value) => {
    const key = String(value);
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key); else next.add(key);
    // Preserve the option order rather than click order so the payload
    // (and any CSV built from it) is stable between edits.
    onChange(options.filter((o) => next.has(String(o.value))).map((o) => o.value));
  };

  const allVisible = visible.length > 0
    && visible.every((o) => chosen.has(String(o.value)));

  const toggleAll = () => {
    if (allVisible) {
      const drop = new Set(visible.map((o) => String(o.value)));
      onChange((selected || []).filter((v) => !drop.has(String(v))));
    } else {
      const next = new Set([...chosen, ...visible.map((o) => String(o.value))]);
      onChange(options.filter((o) => next.has(String(o.value))).map((o) => o.value));
    }
  };

  return (
    <div className="ty-field">
      <label>
        {label}
        <span className="uni-picklist__count">
          {chosen.size} selected
        </span>
      </label>

      <div className="uni-picklist">
        {searchable && (
          <div className="uni-picklist__head">
            <input
              className="ty-input uni-picklist__search"
              type="search"
              value={q}
              placeholder={placeholder}
              onChange={(e) => setQ(e.target.value)}
            />
            <button type="button" className="uni-row-btn" onClick={toggleAll}
                    disabled={visible.length === 0}>
              {allVisible ? 'Clear' : 'All'}
            </button>
          </div>
        )}
        <div className="uni-picklist__body" style={{ maxHeight: height }}>
          {visible.length === 0 ? (
            <div className="uni-picklist__empty">
              {emptyText || 'Nothing to pick.'}
            </div>
          ) : visible.map((o) => (
            <label
              key={String(o.value)}
              className={`uni-picklist__item${o.disabled ? ' is-disabled' : ''}`}
            >
              <input
                type="checkbox"
                checked={chosen.has(String(o.value))}
                disabled={o.disabled}
                onChange={() => toggle(o.value)}
              />
              <span className="uni-picklist__text">
                <span className="uni-picklist__label">{o.label}</span>
                {o.sub && <span className="uni-picklist__sub">{o.sub}</span>}
              </span>
            </label>
          ))}
        </div>
      </div>
      {hint && <span className="uni-field__hint">{hint}</span>}
    </div>
  );
}


/* ====================================================================
   UniOrderedColumnPicker — pick columns and control display order.
   ====================================================================
   Checkbox list for availability plus an ordered list with :: drag
   handles and up/down arrows.  ``selected`` is the ordered key array
   the report engine consumes.
   */

export function UniOrderedColumnPicker({
  label, hint, options, selected, onChange,
  searchable = true, placeholder = 'Search…', height = 190, emptyText,
}) {
  const [q, setQ] = React.useState('');
  const [dragIdx, setDragIdx] = React.useState(null);
  const [dropIdx, setDropIdx] = React.useState(null);

  const ordered = selected || [];
  const chosen = React.useMemo(
    () => new Set(ordered.map(String)), [ordered],
  );
  const optionMap = React.useMemo(() => {
    const map = new Map();
    (options || []).forEach((o) => map.set(String(o.value), o));
    return map;
  }, [options]);

  const visible = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options || [];
    return (options || []).filter((o) => (
      String(o.label || '').toLowerCase().includes(needle)
      || String(o.sub || '').toLowerCase().includes(needle)
    ));
  }, [options, q]);

  const toggle = (value) => {
    const key = String(value);
    if (chosen.has(key)) {
      onChange(ordered.filter((v) => String(v) !== key));
    } else {
      onChange([...ordered, key]);
    }
  };

  const move = (idx, dir) => {
    const next = [...ordered];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  const remove = (idx) => onChange(ordered.filter((_, i) => i !== idx));

  const onDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const onDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropIdx(idx);
  };

  const onDragEnd = () => {
    setDragIdx(null);
    setDropIdx(null);
  };

  const onDrop = (e, targetIdx) => {
    e.preventDefault();
    const from = dragIdx ?? parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(from) || from === targetIdx) {
      onDragEnd();
      return;
    }
    const next = [...ordered];
    const [item] = next.splice(from, 1);
    const insertAt = from < targetIdx ? targetIdx - 1 : targetIdx;
    next.splice(insertAt, 0, item);
    onChange(next);
    onDragEnd();
  };

  const allVisible = visible.length > 0
    && visible.every((o) => chosen.has(String(o.value)));

  const toggleAll = () => {
    if (allVisible) {
      const drop = new Set(visible.map((o) => String(o.value)));
      onChange(ordered.filter((v) => !drop.has(String(v))));
    } else {
      const next = [...ordered];
      visible.forEach((o) => {
        const key = String(o.value);
        if (!chosen.has(key)) next.push(key);
      });
      onChange(next);
    }
  };

  const labelFor = (key) => {
    const opt = optionMap.get(String(key));
    return opt?.label || String(key);
  };

  const subFor = (key) => {
    const opt = optionMap.get(String(key));
    return opt?.sub || '';
  };

  return (
    <div className="ty-field uni-ordered-columns">
      <label>
        {label}
        <span className="uni-picklist__count">
          {ordered.length} selected
        </span>
      </label>

      <div className="uni-picklist">
        {searchable && (
          <div className="uni-picklist__head">
            <input
              className="ty-input uni-picklist__search"
              type="search"
              value={q}
              placeholder={placeholder}
              onChange={(e) => setQ(e.target.value)}
            />
            <button type="button" className="uni-row-btn" onClick={toggleAll}
                    disabled={visible.length === 0}>
              {allVisible ? 'Clear' : 'All'}
            </button>
          </div>
        )}
        <div className="uni-picklist__body" style={{ maxHeight: height }}>
          {visible.length === 0 ? (
            <div className="uni-picklist__empty">
              {emptyText || 'Nothing to pick.'}
            </div>
          ) : visible.map((o) => (
            <label
              key={String(o.value)}
              className={`uni-picklist__item${o.disabled ? ' is-disabled' : ''}`}
            >
              <input
                type="checkbox"
                checked={chosen.has(String(o.value))}
                disabled={o.disabled}
                onChange={() => toggle(o.value)}
              />
              <span className="uni-picklist__text">
                <span className="uni-picklist__label">{o.label}</span>
                {o.sub && <span className="uni-picklist__sub">{o.sub}</span>}
              </span>
            </label>
          ))}
        </div>
      </div>

      {ordered.length > 0 && (
        <div className="uni-ordered-columns__order">
          <div className="uni-ordered-columns__order-title">Column order</div>
          <p className="uni-card__sub">
            Drag by <span className="uni-ordered-columns__handle-preview">::</span>
            {' '}or use the arrows. This order is used left-to-right in the report.
          </p>
          <ol className="uni-ordered-columns__list">
            {ordered.map((key, idx) => (
              <li
                key={`${key}-${idx}`}
                className={[
                  'uni-ordered-columns__item',
                  dragIdx === idx ? 'uni-ordered-columns__item--dragging' : '',
                  dropIdx === idx && dragIdx !== idx
                    ? 'uni-ordered-columns__item--drop-target' : '',
                ].filter(Boolean).join(' ')}
                onDragOver={(e) => onDragOver(e, idx)}
                onDrop={(e) => onDrop(e, idx)}
              >
                <span
                  className="uni-ordered-columns__handle"
                  draggable
                  onDragStart={(e) => onDragStart(e, idx)}
                  onDragEnd={onDragEnd}
                  aria-label="Drag to reorder"
                  title="Drag to reorder"
                >
                  ::
                </span>
                <span className="uni-ordered-columns__text">
                  <span className="uni-ordered-columns__label">{labelFor(key)}</span>
                  {subFor(key) && (
                    <span className="uni-ordered-columns__sub">{subFor(key)}</span>
                  )}
                </span>
                <span className="uni-ordered-columns__actions">
                  <button type="button" className="uni-row-btn" disabled={idx === 0}
                          onClick={() => move(idx, -1)} aria-label="Move up">↑</button>
                  <button type="button" className="uni-row-btn"
                          disabled={idx === ordered.length - 1}
                          onClick={() => move(idx, 1)} aria-label="Move down">↓</button>
                  <button type="button" className="uni-row-btn"
                          onClick={() => remove(idx)} aria-label="Remove">×</button>
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {hint && <span className="uni-field__hint">{hint}</span>}
    </div>
  );
}


/* ====================================================================
   useUniAction — busy/error wrapper for one-off writes.
   ====================================================================
   ``useUniCreate`` covers "open modal → POST a new row".  Plenty of
   screens instead need "click → PATCH/POST an existing row" (save an
   audience, lock a figure, delete a report).  This keeps the busy flag
   and the unwrapped error in one place so pages don't hand-roll
   try/catch/finally each time.
   */

export function useUniAction() {
  const [busy, setBusy]   = React.useState(false);
  const [error, setError] = React.useState(null);

  const run = React.useCallback(async (fn) => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, error, setError, clear: () => setError(null), run };
}


/* ====================================================================
   Row actions
   ====================================================================
   Every list screen needs the same trailing cell: a few small pill
   buttons ("Edit", "Responses", "Delete"), some of which must not be
   offered to this user for this row.  Hand-rolling that per page is how
   pages end up with no actions at all (which is exactly what happened
   on the Forms list) or with a Delete button that 403s on click.

   ``UniRowActions`` takes a declarative array and drops anything the
   row's own permission flags forbid, so a page describes *what* the
   verbs are and never repeats *whether* to show them.

   Each item: ``{ key, label, title, to, href, onClick, show, busy,
   danger, done, disabled }``
     * ``show``     — false ⇒ omitted entirely (not just disabled: an
                      action you may never take is noise, not a hint).
     * ``danger``   — red styling for destructive verbs.
     * ``done``     — green styling for the brief "it worked" state of an
                      action with no other visible result (Copy link).
     * ``to``       — react-router link; ``href`` — plain anchor.
   */

export function UniRowActions({ items }) {
  const visible = (items || []).filter((a) => a && a.show !== false);
  if (visible.length === 0) {
    // A dash keeps the column from collapsing and silently signals
    // "nothing you can do here" rather than looking like a render bug.
    return <span className="uni-num--muted">—</span>;
  }
  return (
    <span className="uni-row-actions">
      {visible.map((a) => {
        const cls = 'uni-row-btn'
          + (a.danger ? ' uni-row-btn--danger' : '')
          + (a.done ? ' uni-row-btn--done' : '');
        const label = a.busy ? '…' : a.label;
        if (a.to) {
          return (
            <Link key={a.key || a.label} to={a.to} className={cls} title={a.title}>
              {label}
            </Link>
          );
        }
        if (a.href) {
          return (
            <a key={a.key || a.label} href={a.href} className={cls} title={a.title}
               target={a.newTab ? '_blank' : undefined}
               rel={a.newTab ? 'noreferrer' : undefined}>
              {label}
            </a>
          );
        }
        return (
          <button
            key={a.key || a.label}
            type="button"
            className={cls}
            title={a.title}
            onClick={a.onClick}
            disabled={a.busy || a.disabled}
          >
            {label}
          </button>
        );
      })}
    </span>
  );
}


/* ====================================================================
   useUniEdit — "click Edit → PATCH the row" state machine.
   ====================================================================
   The mirror image of ``useUniCreate``.  The difference that matters is
   the payload: PATCHing the whole row back would clobber fields the
   modal never showed (and would fail on read-only ones), so ``submit``
   sends only the keys that actually CHANGED against the row we opened.

   Usage:
       const edit = useUniEdit(EP.FORMS, () => reload());
       …
       <button onClick={() => edit.start(row, ['title', 'status'])}>Edit</button>
       <UniModal open={edit.open} …>
   */

export function useUniEdit(collectionUrl, onSaved) {
  const [row, setRow]     = React.useState(null);   // the row being edited
  const [draft, setDraft] = React.useState({});
  const [busy, setBusy]   = React.useState(false);
  const [error, setError] = React.useState(null);

  const start = React.useCallback((record, fields) => {
    const keys = fields && fields.length
      ? fields
      : Object.keys(record || {});
    const initial = {};
    keys.forEach((k) => {
      // Normalise null → '' so controlled inputs don't warn, and so an
      // untouched empty field compares equal on submit.
      initial[k] = record?.[k] ?? '';
    });
    setRow(record);
    setDraft(initial);
    setError(null);
  }, []);

  const close = React.useCallback(() => {
    if (busy) return;
    setRow(null);
    setError(null);
  }, [busy]);

  const set = React.useCallback((key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const submit = React.useCallback(async (override) => {
    if (!row) return null;
    const payload = override || draft;
    // Only the changed keys — see the note above.
    const patch = {};
    Object.entries(payload).forEach(([k, v]) => {
      const before = row[k] ?? '';
      if (JSON.stringify(v) !== JSON.stringify(before)) patch[k] = v;
    });
    if (Object.keys(patch).length === 0) {
      setRow(null);           // nothing to do — treat as a clean cancel
      return null;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await patchJSON(`${collectionUrl}${row.id}/`, patch);
      setRow(null);
      onSaved?.(saved);
      return saved;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setBusy(false);
    }
  }, [collectionUrl, row, draft, onSaved]);

  return {
    open: row !== null, row, draft, busy, error,
    start, close, set, setDraft, submit,
  };
}


/* ====================================================================
   useUniDelete — confirm-then-DELETE, with the error surfaced.
   ====================================================================
   ``window.confirm`` was used ad-hoc across the admin pages, but it has
   no room for context and its "OK" reads the same for "remove a filter"
   as for "destroy 4,000 submissions".  This hook drives a real modal so
   the copy can name the row and warn about what goes with it, and so a
   failed DELETE (e.g. a 403 from the row guard) is shown instead of
   vanishing into the console.
   */

export function useUniDelete(collectionUrl, onDeleted) {
  const [row, setRow]     = React.useState(null);
  const [busy, setBusy]   = React.useState(false);
  const [error, setError] = React.useState(null);

  const ask = React.useCallback((record) => {
    setRow(record);
    setError(null);
  }, []);

  const cancel = React.useCallback(() => {
    if (busy) return;
    setRow(null);
    setError(null);
  }, [busy]);

  const confirm = React.useCallback(async () => {
    if (!row) return false;
    setBusy(true);
    setError(null);
    try {
      await deleteJSON(`${collectionUrl}${row.id}/`);
      setRow(null);
      onDeleted?.(row);
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setBusy(false);
    }
  }, [collectionUrl, row, onDeleted]);

  return { open: row !== null, row, busy, error, ask, cancel, confirm };
}


/**
 * UniConfirmModal — the dialog half of ``useUniDelete``.
 *
 * Kept separate from the hook so a page can word the warning for its own
 * domain ("…and its 128 responses") instead of a generic "Are you sure?".
 */
export function UniConfirmModal({
  state, title, children, confirmLabel = 'Delete', busyLabel = 'Deleting…',
  danger = true,
}) {
  return (
    <UniModal
      open={state.open}
      title={title || 'Delete'}
      onClose={state.cancel}
      width={460}
      footer={
        <>
          <button type="button" className="ty-btn ty-btn--secondary"
                  onClick={state.cancel} disabled={state.busy}>
            Cancel
          </button>
          <button type="button"
                  className={danger ? 'ty-btn ty-btn--danger' : 'ty-btn'}
                  onClick={state.confirm} disabled={state.busy}>
            {state.busy ? busyLabel : confirmLabel}
          </button>
        </>
      }
    >
      <UniFormError error={state.error} />
      {children}
    </UniModal>
  );
}


/* ====================================================================
   Detail rows — the read-only "View" counterpart to the field helpers.
   ==================================================================== */

/** One ``Label / value`` line inside a view modal. */
export function UniDetailRow({ label, children }) {
  return (
    <div className="uni-detail-row">
      <div className="uni-detail-row__label">{label}</div>
      <div className="uni-detail-row__value">
        {children === null || children === undefined || children === ''
          ? <span className="uni-num--muted">—</span>
          : children}
      </div>
    </div>
  );
}


/* ====================================================================
   Spec-driven view / edit modals
   ====================================================================

   Uni Email and Uni SMS between them have eight list screens whose
   Edit dialogs are all the same dialog with different labels: a few
   text inputs, a status dropdown, a couple of checkboxes.  Hand-writing
   eight near-identical modals is how they'd drift apart — one grows a
   trim() the others don't, one forgets to disable Save while busy.

   So instead each page declares *what* its fields are and these two
   components own *how* they're rendered and submitted.  A field spec is:

     { key, label, type, options, hint, required, placeholder, rows,
       render, editable }

   ``type`` is one of 'text' | 'textarea' | 'select' | 'check' | 'number'.
   ``render`` (view modal only) formats the value — default is a string
   cast with booleans promoted to ✓/✗ ticks and nullish → em-dash.

   Uni Forms keeps its bespoke modals: they have cross-field behaviour
   (a poll warning that depends on ``vote_count``, a quiz pass-mark that
   only applies to one quiz type) that a declarative spec shouldn't try
   to express.                                                          */

/** Field keys a spec considers editable — feed straight to ``edit.start``. */
export function uniSpecKeys(fields) {
  return (fields || [])
    .filter((f) => f && (f.editable !== false || f.type === 'copy'))
    .map((f) => f.key);
}


/** Coerce a draft value to what DRF expects for the given field type. */
function specCoerce(field, value) {
  if (field.type === 'check')  return !!value;
  if (field.type === 'number') {
    if (value === '' || value === null || value === undefined) return null;
    return Number(value);
  }
  // Trim text so a stray space can't create a "different" name.
  return typeof value === 'string' ? value.trim() : value;
}


/** Render one spec field bound to a draft object. */
function SpecField({ field, draft, set }) {
  const value = draft[field.key];
  switch (field.type) {
    case 'textarea':
      return (
        <UniTextArea
          label={field.label} hint={field.hint} help={field.help} rows={field.rows}
          placeholder={field.placeholder}
          value={value ?? ''} onChange={(v) => set(field.key, v)}
        />
      );
    case 'select':
      return (
        <UniSelectField
          label={field.label} hint={field.hint} help={field.help} required={field.required}
          options={field.options || []}
          value={value ?? ''} onChange={(v) => set(field.key, v)}
        />
      );
    case 'check':
      return (
        <UniCheckField
          label={field.label} hint={field.hint} help={field.help}
          checked={!!value} onChange={(v) => set(field.key, v)}
        />
      );
    case 'number':
      return (
        <UniTextField
          label={field.label} hint={field.hint} help={field.help} required={field.required}
          placeholder={field.placeholder}
          value={value === null || value === undefined ? '' : String(value)}
          // Digits (and one leading minus) only, so the input can't send
          // DRF something it'll reject with a 400.
          onChange={(v) => set(field.key, v.replace(/[^0-9.-]/g, ''))}
        />
      );
    case 'copy':
      return (
        <UniCopyField
          label={field.label}
          hint={field.hint}
          help={field.help}
          value={value || ''}
          open={Boolean(field.open)}
        />
      );
    default:
      return (
        <UniTextField
          label={field.label} hint={field.hint} help={field.help} required={field.required}
          placeholder={field.placeholder} autoFocus={!!field.autoFocus}
          type={field.type === 'password' ? 'password' : 'text'}
          value={value ?? ''} onChange={(v) => set(field.key, v)}
        />
      );
  }
}


/**
 * Generic Edit modal driven by a field spec.
 *
 * Save is disabled until every ``required`` field has a value, which
 * catches the common 400 (blank name) before it costs a round-trip.
 */
export function UniSpecEditModal({ edit, title = 'Edit', fields, width }) {
  const list = (fields || []).filter((f) => {
    if (!f) return false;
    if (typeof f.when === 'function' && edit.row && !f.when(edit.row)) return false;
    return f.editable !== false || f.type === 'copy';
  });
  const missing = list.some(
    (f) => f.type !== 'copy' && f.editable !== false && f.required && !String(edit.draft?.[f.key] ?? '').trim(),
  );

  const save = () => {
    const payload = {};
    list.forEach((f) => {
      if (f.type === 'copy' || f.editable === false) return;
      payload[f.key] = specCoerce(f, edit.draft?.[f.key]);
    });
    edit.submit(payload);
  };

  return (
    <UniModal
      open={edit.open}
      title={title}
      onClose={edit.close}
      width={width}
      footer={
        <>
          <button type="button" className="ty-btn ty-btn--secondary"
                  onClick={edit.close} disabled={edit.busy}>
            Cancel
          </button>
          <button type="button" className="ty-btn"
                  onClick={save} disabled={edit.busy || missing}>
            {edit.busy ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <UniFormError error={edit.error} />
      {list.map((f, i) => (
        <SpecField
          key={f.key}
          field={i === 0 ? { autoFocus: true, ...f } : f}
          draft={edit.draft || {}}
          set={edit.set}
        />
      ))}
    </UniModal>
  );
}


/**
 * Generic read-only View modal driven by the same field spec.
 *
 * ``row`` doubles as the open flag — passing null closes it — which
 * keeps the caller down to one ``useState``.
 */
export function UniSpecViewModal({ row, onClose, title = 'Details', fields, width = 560 }) {
  return (
    <UniModal
      open={!!row}
      title={title}
      onClose={onClose}
      width={width}
      footer={
        <button type="button" className="ty-btn ty-btn--secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      {row && (fields || []).map((f) => {
        if (typeof f.when === 'function' && !f.when(row)) return null;
        const raw = row[f.key];
        if (f.type === 'copy') {
          return (
            <UniCopyField
              key={f.key}
              label={f.label}
              hint={f.hint}
              help={f.help}
              value={raw || ''}
              open={Boolean(f.open)}
            />
          );
        }
        let display;
        if (f.render)                     display = f.render(row);
        else if (typeof raw === 'boolean') display = <UniBool value={raw} />;
        else if (f.type === 'select' && f.options) {
          // Show the human label, not the stored enum value.
          const hit = f.options.find((o) => o.value === raw);
          display = hit ? hit.label : raw;
        } else                             display = raw;
        return (
          <UniDetailRow key={f.key} label={f.label}>{display}</UniDetailRow>
        );
      })}
    </UniModal>
  );
}




/* ====================================================================
   Copy-to-clipboard
   ====================================================================
   A shareable link is only useful if it can leave the app, and the way
   people move a link is Ctrl-C.  Asking them to select a truncated
   ``<code>`` inside a table cell by hand is how half-copied URLs get
   pasted into emails.

   Two problems make this less trivial than ``navigator.clipboard``:

   * The Clipboard API is unavailable on insecure origins (plain HTTP
     dev hosts) and blocked outright by some corporate browser policies.
     ``copyToClipboard`` falls back to the legacy ``execCommand('copy')``
     path so the button never silently does nothing.
   * A copy produces no visible change, so without feedback users click
     it repeatedly, unsure whether it worked.  ``useUniCopy`` owns that
     "Copied!" flash (and its timer cleanup) once, instead of every page
     re-inventing it with a ``setTimeout`` that leaks on unmount.
   */

/**
 * Put ``text`` on the clipboard.  Resolves true on success.
 *
 * Never throws: the caller is a button, and a rejected promise there
 * would be an unhandled rejection in the console for something the user
 * can still do by hand.
 */
export async function copyToClipboard(text) {
  const value = String(text ?? '');
  if (!value) return false;

  // Preferred path — available on https:// and on localhost.
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      /* fall through to the legacy path below */
    }
  }

  // Legacy fallback.  The textarea must be in the document and selected
  // for ``execCommand`` to see anything, but it must not scroll the page
  // or flash on screen — hence the off-screen fixed position rather than
  // ``display: none`` (a hidden node cannot be selected).
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}


/**
 * "Click → copy → flash Copied!" state for one or many targets.
 *
 * ``copy(text, key)`` copies and marks ``key`` as freshly copied for
 * ``resetMs``.  The key exists so a *list* of copy buttons can share one
 * hook and still light up only the row that was clicked — pass the row
 * id.  Single-button callers can omit it.
 *
 * ``copiedKey`` is the row currently flashing (``true`` for the keyless
 * case) and ``failed`` is set when the browser refused the copy, so the
 * UI can tell the user to copy the visible text by hand instead of
 * claiming success.
 */
export function useUniCopy(resetMs = 1600) {
  const [copiedKey, setCopiedKey] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const timer = React.useRef(null);

  // A copy that flashes for 1.6s on a row the user then deletes would
  // fire setState on an unmounted component — clear the timer instead.
  React.useEffect(() => () => clearTimeout(timer.current), []);

  const copy = React.useCallback(async (text, key = true) => {
    const ok = await copyToClipboard(text);
    clearTimeout(timer.current);
    setFailed(!ok);
    setCopiedKey(ok ? key : null);
    timer.current = setTimeout(() => {
      setCopiedKey(null);
      setFailed(false);
    }, resetMs);
    return ok;
  }, [resetMs]);

  const isCopied = React.useCallback(
    (key = true) => copiedKey !== null && copiedKey === key,
    [copiedKey],
  );

  return { copy, copiedKey, isCopied, failed };
}


/** Platform-appropriate name for the manual-copy shortcut. */
function copyShortcut() {
  const platform = navigator.userAgentData?.platform || navigator.platform || '';
  return /mac|iphone|ipad|ipod/i.test(platform) ? '⌘C' : 'Ctrl+C';
}


/**
 * Read-only text box + Copy button (+ optional Open) for a URL.
 *
 * Used in the view drawers where the whole point is to see the link in
 * full and take it away with you.  The input is ``readOnly`` rather than
 * ``disabled``: a disabled input cannot be selected, which would defeat
 * the manual-copy fallback when the Clipboard API is blocked.
 *
 * Props
 * -----
 * value    : the URL (nothing renders when falsy)
 * label    : caption above the box (hidden when compact)
 * hint     : small print under it
 * open     : show an "Open" link that loads the URL in a new tab
 * compact  : input + Copy only (table cells)
 * onCopied : optional callback after a successful copy
 */
export function UniCopyField({
  value, label = 'Link', hint, help, open = true, compact = false, onCopied,
}) {
  // Both hooks run before the early return below — bailing out first
  // would change the hook order between renders (the field appears the
  // moment a form is published) and React would throw.
  const { copy, isCopied, failed } = useUniCopy();
  const id = React.useId();
  if (!value) return null;

  const copied = isCopied();
  const row = (
    <>
      <div className="uni-copy-row">
        <input
          id={id}
          className="ty-input uni-copy-row__input"
          type="text"
          value={value}
          readOnly
          aria-label={label}
          // Selecting the whole URL on focus makes the manual Ctrl-C
          // route a single click too.
          onFocus={(e) => e.target.select()}
        />
        <button
          type="button"
          className={'ty-btn ty-btn--sm uni-copy-btn'
            + (copied ? ' uni-copy-btn--done' : '')}
          onClick={async () => {
            const ok = await copy(value);
            if (ok) onCopied?.(value);
          }}
          title="Copy this link to the clipboard"
        >
          {copied ? '✓ Copied' : '🔗 Copy'}
        </button>
        {open && (
          <a
            className="ty-btn ty-btn--sm ty-btn--secondary uni-copy-open"
            href={value}
            target="_blank"
            rel="noreferrer"
            title="Open this link in a new tab"
          >
            Open ↗
          </a>
        )}
      </div>
      {failed && (
        <span className="uni-field__hint uni-copy-failed">
          Your browser blocked the clipboard — select the link above and
          press {copyShortcut()}.
        </span>
      )}
    </>
  );

  if (compact) return row;
  return (
    <UniField label={label} hint={hint} help={help} htmlFor={id}>
      {row}
    </UniField>
  );
}


